import React, { useEffect, useState, useRef, useCallback } from 'react'

// Confetti colors - vibrant and celebratory
const CONFETTI_COLORS = [
  '#ff6b6b', // Red
  '#4ecdc4', // Teal
  '#45b7d1', // Sky blue
  '#96ceb4', // Sage green
  '#ffeaa7', // Yellow
  '#ff9ff3', // Pink
  '#54a0ff', // Blue
  '#5f27cd', // Purple
  '#00d2d3', // Cyan
  '#ff9f43', // Orange
]

const WeekResultAnimation = ({ result, weekDisplay, onComplete }) => {
  const [isVisible, setIsVisible] = useState(true)
  const [isFading, setIsFading] = useState(false)
  const canvasRef = useRef(null)
  const animationRef = useRef(null)
  const particlesRef = useRef([])

  const isWin = result === 'win'

  // Create particles based on result type
  const createParticles = useCallback((canvas) => {
    const particles = []
    const particleCount = isWin ? 180 : 250

    for (let i = 0; i < particleCount; i++) {
      if (isWin) {
        // Confetti particles - varied shapes and behaviors
        const shapeType = Math.floor(Math.random() * 3) // 0: rectangle, 1: circle, 2: triangle
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * -canvas.height, // Start above viewport
          size: Math.random() * 12 + 4,
          speedY: Math.random() * 4 + 2,
          speedX: Math.random() * 4 - 2,
          rotation: Math.random() * 360,
          rotationSpeed: Math.random() * 15 - 7.5,
          color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
          shapeType,
          wobble: Math.random() * Math.PI * 2,
          wobbleSpeed: Math.random() * 0.1 + 0.05,
          opacity: 1,
        })
      } else {
        // Rain particles - streaky and atmospheric
        particles.push({
          x: Math.random() * (canvas.width + 100) - 50,
          y: Math.random() * -canvas.height,
          length: Math.random() * 25 + 15,
          speedY: Math.random() * 12 + 18,
          speedX: -2 - Math.random() * 2, // Slight wind effect
          opacity: Math.random() * 0.4 + 0.2,
          thickness: Math.random() * 1.5 + 0.5,
        })
      }
    }

    return particles
  }, [isWin])

  // Draw a confetti particle
  const drawConfetti = (ctx, p) => {
    ctx.save()
    ctx.translate(p.x, p.y)
    ctx.rotate((p.rotation * Math.PI) / 180)
    ctx.globalAlpha = p.opacity

    ctx.fillStyle = p.color

    switch (p.shapeType) {
      case 0: // Rectangle
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2)
        break
      case 1: // Circle
        ctx.beginPath()
        ctx.arc(0, 0, p.size / 3, 0, Math.PI * 2)
        ctx.fill()
        break
      case 2: // Triangle
        ctx.beginPath()
        ctx.moveTo(0, -p.size / 2)
        ctx.lineTo(p.size / 2, p.size / 2)
        ctx.lineTo(-p.size / 2, p.size / 2)
        ctx.closePath()
        ctx.fill()
        break
      default:
        break
    }

    ctx.restore()
  }

  // Draw a rain drop
  const drawRain = (ctx, p) => {
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + p.speedX * 0.5, p.y + p.length)

    const gradient = ctx.createLinearGradient(p.x, p.y, p.x, p.y + p.length)
    gradient.addColorStop(0, `rgba(150, 170, 200, 0)`)
    gradient.addColorStop(0.3, `rgba(150, 170, 200, ${p.opacity * 0.5})`)
    gradient.addColorStop(1, `rgba(180, 200, 230, ${p.opacity})`)

    ctx.strokeStyle = gradient
    ctx.lineWidth = p.thickness
    ctx.lineCap = 'round'
    ctx.stroke()
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')

    // Set canvas size
    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    // Initialize particles
    particlesRef.current = createParticles(canvas)

    let lastTime = performance.now()

    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 16.67 // Normalize to ~60fps
      lastTime = currentTime

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw background overlay for rain
      if (!isWin) {
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height)
        gradient.addColorStop(0, 'rgba(40, 50, 70, 0.25)')
        gradient.addColorStop(1, 'rgba(30, 40, 60, 0.4)')
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, canvas.width, canvas.height)
      }

      particlesRef.current.forEach((p) => {
        if (isWin) {
          // Update confetti
          drawConfetti(ctx, p)

          p.y += p.speedY * deltaTime
          p.x += (p.speedX + Math.sin(p.wobble) * 2) * deltaTime
          p.rotation += p.rotationSpeed * deltaTime
          p.wobble += p.wobbleSpeed * deltaTime

          // Slow down as it falls (air resistance effect)
          p.speedY *= 0.998
          p.speedX *= 0.99

          // Reset if off screen
          if (p.y > canvas.height + p.size) {
            p.y = -p.size * 2
            p.x = Math.random() * canvas.width
            p.speedY = Math.random() * 4 + 2
            p.speedX = Math.random() * 4 - 2
          }
        } else {
          // Update rain
          drawRain(ctx, p)

          p.y += p.speedY * deltaTime
          p.x += p.speedX * deltaTime

          // Reset if off screen
          if (p.y > canvas.height + p.length) {
            p.y = -p.length
            p.x = Math.random() * (canvas.width + 100) - 50
          }
        }
      })

      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    // Start fade after 3.5 seconds
    const fadeTimer = setTimeout(() => {
      setIsFading(true)
    }, 3500)

    // Complete after 4 seconds (0.5 second fade)
    const completeTimer = setTimeout(() => {
      setIsVisible(false)
      onComplete()
    }, 4000)

    return () => {
      window.removeEventListener('resize', resize)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      clearTimeout(fadeTimer)
      clearTimeout(completeTimer)
    }
  }, [isWin, createParticles, onComplete])

  if (!isVisible) return null

  return (
    <div className={`week-result-animation ${isFading ? 'fading' : ''} ${isWin ? 'win' : 'loss'}`}>
      <canvas ref={canvasRef} className="week-result-canvas" />
      <div className="week-result-text-container">
        <div className={`week-result-message ${isWin ? 'win-message' : 'loss-message'}`}>
          {isWin ? (
            <>
              <span className="result-emoji">🎉</span>
              <span className="result-text">You won the week starting {weekDisplay}!</span>
            </>
          ) : (
            <>
              <span className="result-emoji">😔</span>
              <span className="result-text">You lost the week starting {weekDisplay}...</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default WeekResultAnimation